/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers/lib/Diagnostics';
import { EpisodeInfo } from 'client-code/episode-guide';
import { UpcomingOffer, UpcomingOfferV2 } from 'client-code/upcoming-offers';
import { VodItem } from 'client-code/vod-browse';

import { DeviceModel, Settings } from '../../core/device';
import { BrandingUiInfoKeys } from '../../core/entities';
import { NdvrInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import {
    CursorManager,
    KeyManager,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { NetworkManager } from '../../core/network/NetworkManager';
import { SharedPrefConstants } from '../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { ListView, ListViewInfoCardUserInputHandler } from '../../core/usecases';
import { BrandingUiUtility } from '../../core/utils';
import { clearActiveInteractiveComponent } from '../../store/interactivelayer/interactiveLayer.reducers';
import { TListViewScreen } from '../../store/listscreenview';
import { setListEntryPageActions } from '../../store/listscreenview/listviewscreen.actions';
import {
    listScreenSetCaretInvisible,
    onInfoCardDismissListView,
} from '../../store/listscreenview/listviewscreen.reducers';
import { showWhisper } from '../../store/whisper/whisper.reducer';
import { ClockTypes } from '../Clock';
import { AntiburnOsd, ScreenHeader } from '../common';
import { useInfoCard, useScreenController } from '../hooks';
import { Whisper } from '../Whisper';
import { WhisperTypes } from '../Whisper/WhisperTypes';
import { AllEpisodesList } from './AllEpisodesList';
import { AllEpisodesListUtility } from './AllEpisodesList/AllEpisodesListUtility';
import { InteractiveLayer } from './InteractiveLayer';
import OnDemandList from './OnDemandList/OnDemandList';
import UpcomingList from './UpcomingList/UpcomingList';
import VodListView from './VodListView/VodListView';

import './ListViewScreen.scss';

const ListViewScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'ListViewScreen';

    const MAX_ELEMENTS_IN_ONE_PAGE_FOR_ON_DEMAND = 19;

    const dispatch = useTivoDispatch();
    const [hasError, setHasError] = useState<boolean>(false);

    const [errorMessage, setErrorMessage] = useState<string>();

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const ListViewScreenIns = useScreenController<TListViewScreen>({
        identifier: TAG,
        properties: {
            screenController: ScreenControllerTypes.LIST_VIEW_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK,
        },
    });
    const { isActive: isAntiBurnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const { id, title, listType, isAdult } = ListViewScreenIns?.screen?.getProperties() || {};
    const { position, limit, page, ghostMode } = useTivoSelector(
        (state: RootState) => state.listViewScreen,
    );
    const [items, setItems] = useState<
        VodItem[] | UpcomingOffer[] | UpcomingOfferV2[] | EpisodeInfo[] | []
    >();
    const [seasonData, setSeasonData] = useState<any>(undefined);
    const offerIdRef = React.useRef<string>('');
    const allEpisodesPageRestoredRef = useRef<string>('');
    const list = useMemo(() => new ListView(), []);
    const settingsService = useMemo(() => new Settings(), []);
    const isVideoWindowEnabled = useMemo(
        () => settingsService.isVideoWindowEnabled(),
        [settingsService],
    );

    const handleListViewInit = async () => {
        try {
            if (!id) return;
            if (listType === 'upcoming') {
                const { upcomingOffers } = await list.getUpcomingData(id || '');
                const newItems = upcomingOffers || [];
                setItems(newItems);
            } else if (
                listType === 'myRentals' ||
                listType === 'myadultRentals' ||
                listType === 'textCurrentRentals'
            ) {
                const { vodItemList } = await list.getMyRentalsVodBrowseData(
                    id || '',
                    listType === 'myadultRentals',
                    page * limit,
                    (page + 1) * limit,
                );
                const newItems = vodItemList?.vodItemList || [];
                setItems(newItems);
                offerIdRef.current =
                    (newItems && (newItems[position]?.vodItemKernel as any))?.offerId || '';
            } else {
                Log.w(TAG, `Unsupported listType: ${listType}`);
            }
        } catch (error: any) {
            setHasError(true);
            const message = error.message.replace('SCREEN_TYPE', title || '');
            setErrorMessage(message);
            dispatch(showWhisper({ message, iconType: WhisperTypes.INFO }));
            setItems([]);
        }
    };

    const onCursorStateChangeCallback = (state: boolean) => {
        if (state === false) {
            dispatch(clearActiveInteractiveComponent());
            dispatch(listScreenSetCaretInvisible());
        }
    };

    useInfoCard(ListViewInfoCardUserInputHandler.name, () => {
        dispatch(onInfoCardDismissListView());
    });

    useEffect(() => {
        KeyManager.getInstance().stopKeyIgnoring();
        const refCursormanager = CursorManager.subscribe(onCursorStateChangeCallback);
        handleListViewInit();
        Diagnostics.info(TAG, `${title} mounted.`);
        return () => {
            CursorManager.unsubscribe(refCursormanager);
            setItems([]);
            setHasError(false);
            Diagnostics.info(TAG, `${title} unmounted.`);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Restore persisted page for allEpisodes before fetching data
        if (listType === 'allEpisodes' && id && allEpisodesPageRestoredRef.current !== id) {
            const sharedPreferences = DeviceModel.getSharedPreferences(
                SharedPrefConstants.LIST_SCREEN_PREF_KEY,
            );
            const listViewState =
                sharedPreferences.getJson(SharedPrefConstants.LISTVIEW_STATE) || {};
            const lastPage = listViewState[id]?.lastPage;
            const restoredPage = lastPage !== undefined && lastPage !== '' ? lastPage : 0;

            allEpisodesPageRestoredRef.current = id;

            if (restoredPage !== page) {
                dispatch(setListEntryPageActions(restoredPage));
                return; // Skip fetch; effect will re-fire with the correct page
            }
        }

        const fetchListData = async () => {
            const listScreenSharedPreferenceEditor = DeviceModel.getSharedPreferences(
                SharedPrefConstants.LIST_SCREEN_PREF_KEY,
            ).edit();
            try {
                if (!id) return;
                if (listType === 'upcoming') {
                    const { upcomingOffers, totalCount } = await list.getUpcomingData(
                        id || '',
                        page * limit,
                        limit,
                    );

                    const newItems = upcomingOffers || [];
                    setItems(newItems);
                    listScreenSharedPreferenceEditor.putJson('list', newItems as unknown as JSON);
                    listScreenSharedPreferenceEditor.putNumber(
                        SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT,
                        totalCount,
                    );
                    offerIdRef.current = newItems && (newItems[position]?.offerId || '');
                } else if (
                    listType === 'myRentals' ||
                    listType === 'myadultRentals' ||
                    listType === 'textCurrentRentals'
                ) {
                    const { vodItemList, totalCount } = await list.getMyRentalsVodBrowseData(
                        id || '',
                        isAdult ?? false,
                        page * limit,
                        (page + 1) * limit,
                    );
                    const newItems = vodItemList?.vodItemList || [];
                    listScreenSharedPreferenceEditor.putJson('list', newItems as unknown as JSON);
                    listScreenSharedPreferenceEditor.putNumber(
                        SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT,
                        totalCount,
                    );
                    setItems(newItems);
                    offerIdRef.current =
                        (newItems && (newItems[position]?.vodItemKernel as any))?.offerId || '';
                } else if (listType === 'onDemand') {
                    const currScreen = ScreenController.getInstance().getScreen();
                    const currScreenProperties = currScreen.getProperties() as any;
                    const {
                        episodes,
                        seasonData: fetchedSeasonData,
                        totalCount,
                    } = await list.getSeasonData(
                        id,
                        currScreenProperties.partnerId,
                        currScreenProperties.collapsedVodItemId,
                        page * MAX_ELEMENTS_IN_ONE_PAGE_FOR_ON_DEMAND,
                        (page + 1) * MAX_ELEMENTS_IN_ONE_PAGE_FOR_ON_DEMAND,
                    );
                    listScreenSharedPreferenceEditor.putJson('list', episodes as unknown as JSON);
                    listScreenSharedPreferenceEditor.putNumber(
                        SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT,
                        totalCount,
                    );
                    setItems(episodes);
                    setSeasonData(fetchedSeasonData);
                    setIsLoading(false);
                } else if (listType === 'allEpisodes') {
                    const allEpisodesData = await list.getAllEpisodesData({
                        unifiedCollectionId: id,
                        sort: 'seasonOrYearAsc',
                        offset: page * AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES,
                        limit: AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES,
                    });
                    setItems(allEpisodesData);
                    setIsLoading(false);
                } else {
                    listScreenSharedPreferenceEditor.putJson('list', [] as any);
                    Log.w(TAG, `Unsupported listType: ${listType}`);
                }
            } catch (error: any) {
                setItems([]);
                let message = error.message.replace('SCREEN_TYPE', title || '');
                if (NetworkManager.isNetworkDisconnected()) {
                    const service = new NdvrInteractor();
                    message = BrandingUiUtility.formatDescription(
                        Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
                        {
                            DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                                BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                            ),
                            BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                                ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                                : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
                        },
                    );
                }
                setHasError(true);
                dispatch(showWhisper({ message, iconType: WhisperTypes.INFO }));
                setErrorMessage(message);
                listScreenSharedPreferenceEditor.putJson('list', [] as any);
            } finally {
                listScreenSharedPreferenceEditor.commit();
                setIsLoading(false);
            }
        };
        setIsLoading(true);
        setItems([]);
        setSeasonData(undefined);
        setIsLoading(true);
        fetchListData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, id]);

    useEffect(() => {
        if (!items || (items && items?.length === 0)) return;
        const currentItem = items[position];

        if (currentItem && 'vodItemKernel' in currentItem && currentItem.vodItemKernel) {
            offerIdRef.current = (currentItem.vodItemKernel as any).offerId || '';
        } else if (currentItem && 'offerId' in currentItem) {
            offerIdRef.current = (currentItem as any).offerId || '';
        } else {
            offerIdRef.current = '';
        }
        const selectedItem = { ...(items && items[position]), type: listType };
        const listScreenSharedPreferenceEditor = DeviceModel.getSharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).edit();
        listScreenSharedPreferenceEditor.putJson(
            SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION,
            selectedItem as unknown as JSON,
        );
        listScreenSharedPreferenceEditor.commit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, position]);

    return (
        <div className="list-wrapper">
            <ScreenHeader title={title || ''} clockType={ClockTypes.DEFAULT_CLOCK} />
            <div className="list">
                {hasError ? (
                    <span className={`error-message ${isVideoWindowEnabled ? 'adjust-width' : ''}`}>
                        {errorMessage}
                    </span>
                ) : (
                    <div key={`${position}_list-view`}>
                        {listType === 'myRentals' ||
                            listType === 'myadultRentals' ||
                            (listType === 'textCurrentRentals' && (
                                <VodListView
                                    items={items}
                                    position={position}
                                    ghostMode={ghostMode}
                                />
                            ))}
                        {listType === 'upcoming' && items && items.length > 0 && (
                            <UpcomingList
                                items={items}
                                selectedIndex={position}
                                ghostMode={ghostMode}
                            />
                        )}
                        {listType === 'onDemand' && (
                            <OnDemandList
                                episodes={items as EpisodeInfo[]}
                                seasonData={seasonData}
                                title={title || ''}
                                isLoading={isLoading}
                                ghostMode={ghostMode}
                            />
                        )}
                        {listType === 'allEpisodes' && (
                            <AllEpisodesList
                                episodes={items as EpisodeInfo[]}
                                title={title || ''}
                                isLoading={isLoading}
                                ghostMode={ghostMode}
                            />
                        )}
                    </div>
                )}
            </div>
            {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                <InteractiveLayer />
            ) : null}
            {isAntiBurnOsdActive && <AntiburnOsd />}
            <Whisper message="" />
        </div>
    );
};

export default ListViewScreen;
